import type {
	AssistantMessage,
	MultiActionConfig,
	ContactScrapeConfig,
	WriteProposal,
	EmailDraft,
	OfferDraft,
	ToolProposal,
	ProspectingAddConfig
} from '../../Assistant/types';

// The one transcript reducer for the AI Workspace chat surfaces (WEB-7957).
// Every optimistic mutation the chat controllers apply to the visible message
// list lives here — ordering-aware assistant-text insertion, same-turn
// proposal coalescing, approval-card status updates — so both surfaces share
// identical transcript semantics. Streaming buffers are NOT state here; they
// live in useAgentStream and only land in the list via insertAssistantText
// when a stream commits.

type MessagesAction =
	// Atomic swap on conversation (re)load — also used to clear ([]).
	| { type: 'set'; messages: AssistantMessage[] }
	// Optimistic user message / card-only assistant message (chart, email
	// draft, offer draft, multi-action). Cards always append at the end: they
	// arrive in stream order and sit above the text reply that follows.
	| { type: 'append'; message: AssistantMessage }
	// Assistant TEXT commit — inserted before trailing same-turn cards when
	// it's the turn's first text (see insertAssistantText below).
	| { type: 'insertAssistantText'; message: AssistantMessage }
	// A write proposal streamed in — coalesced into the current turn's group.
	| { type: 'addWriteProposal'; proposal: WriteProposal; runId: string | null }
	| { type: 'updateMultiAction'; id: string; updates: Partial<MultiActionConfig> }
	| { type: 'updateContactScrape'; id: string; updates: Partial<ContactScrapeConfig> }
	| { type: 'updateWriteProposal'; index: number; update: Partial<WriteProposal> }
	| { type: 'updateWriteProposalGroupItem'; messageIndex: number; itemIndex: number; update: Partial<WriteProposal> }
	| {
			type: 'updateWriteProposalGroupEmailItem';
			messageIndex: number;
			itemIndex: number;
			update: Partial<EmailDraft>;
	  }
	| { type: 'updateEmailDraft'; index: number; update: Partial<EmailDraft> }
	| { type: 'updateOfferDraft'; index: number; update: Partial<OfferDraft> }
	| { type: 'updateToolProposal'; index: number; update: Partial<ToolProposal> }
	| { type: 'updateProspectingAdd'; index: number; update: Partial<ProspectingAddConfig> }
	// The server replaced the optimistic data-URL images / attachments on the
	// just-sent user message with their uploaded counterparts.
	| { type: 'setLastUserImages'; urls: string[] }
	| { type: 'setLastUserAttachments'; attachments: NonNullable<AssistantMessage['attachments']> }
	// Regenerate — drop the reply (and everything after it) being re-run.
	| { type: 'truncateFrom'; index: number }
	// Re-attach streamed reasoning to the reloaded assistant message.
	// assistant_message has no thinking column — thinking is streamed, not
	// persisted — so a post-run reload would otherwise drop it.
	| { type: 'attachThinkingToLastAssistant'; thinking: string }
	// Pages build ledger (WEB-10046). Opaque here for the same reason it is on
	// AssistantMessage: the reducer only carries it, and every other surface
	// never dispatches this.
	| { type: 'attachActivitiesToLastAssistant'; activities: unknown };

// Proposals/cards can arrive mid-stream BEFORE the streaming text is
// persisted. Naively appending puts the final text below the card, reversing
// the chronological order the user actually saw. So when persisting the FIRST
// assistant text of a turn, insert it before any trailing cards from that
// turn. For follow-up texts (e.g. the agent's post-Accept confirmation),
// append at the end so they land below the card.
const insertAssistantText = (prev: AssistantMessage[], msg: AssistantMessage): AssistantMessage[] => {
	const isCard = (m: AssistantMessage) =>
		!!m.writeProposalGroup ||
		!!m.writeProposal ||
		!!m.multiAction ||
		!!m.contactScrape ||
		!!m.emailDraft ||
		!!m.offerDraft ||
		!!m.toolProposal ||
		!!m.prospectingAdd;
	let lastUserIdx = -1;
	for (let i = prev.length - 1; i >= 0; i--) {
		if (prev[i].role === 'user') {
			lastUserIdx = i;
			break;
		}
	}
	let hasIntroText = false;
	for (let i = lastUserIdx + 1; i < prev.length; i++) {
		const m = prev[i];
		// A message that already has visible text counts as the turn's intro text
		// even if it ALSO carries a card (e.g. a write proposal attached to the
		// same assistant message). Without this, the post-approval follow-up would
		// be inserted ABOVE the proposal card instead of appended below it.
		if (m.role === 'assistant' && m.content?.trim()) {
			hasIntroText = true;
			break;
		}
	}
	if (hasIntroText) return [...prev, msg];
	let firstCardIdx = -1;
	for (let i = lastUserIdx + 1; i < prev.length; i++) {
		if (isCard(prev[i])) {
			firstCardIdx = i;
			break;
		}
	}
	if (firstCardIdx === -1) return [...prev, msg];
	return [...prev.slice(0, firstCardIdx), msg, ...prev.slice(firstCardIdx)];
};

// Coalesce ALL proposals from the current turn into one group, no matter what
// messages arrive between them (text, tool calls, etc.). Walk backwards from
// the end and stop at the most recent user message — anything before it is a
// previous turn and must not merge.
const addWriteProposal = (
	prev: AssistantMessage[],
	proposal: WriteProposal,
	runId: string | null
): AssistantMessage[] => {
	let sameTurnIdx = -1;
	for (let i = prev.length - 1; i >= 0; i--) {
		const m = prev[i];
		if (m.role === 'user') break;
		if (m.writeProposalGroup || m.writeProposal) {
			sameTurnIdx = i;
			break;
		}
	}

	if (sameTurnIdx >= 0) {
		const target = prev[sameTurnIdx];
		const next = prev.slice();
		if (target.writeProposalGroup) {
			next[sameTurnIdx] = {
				...target,
				writeProposalGroup: {
					items: [...target.writeProposalGroup.items, { kind: 'write', proposal }]
				}
			};
		} else if (target.writeProposal) {
			const { writeProposal: prevProposal, ...rest } = target;
			next[sameTurnIdx] = {
				...rest,
				writeProposalGroup: {
					items: [
						{ kind: 'write', proposal: prevProposal },
						{ kind: 'write', proposal }
					]
				}
			};
		}
		return next;
	}

	return [
		...prev,
		{
			role: 'assistant',
			content: '',
			metadata: runId ? { runId } : undefined,
			writeProposalGroup: { items: [{ kind: 'write', proposal }] }
		}
	];
};

const messagesReducer = (state: AssistantMessage[], action: MessagesAction): AssistantMessage[] => {
	switch (action.type) {
		case 'set':
			return action.messages;
		case 'append':
			return [...state, action.message];
		case 'insertAssistantText':
			return insertAssistantText(state, action.message);
		case 'addWriteProposal':
			return addWriteProposal(state, action.proposal, action.runId);
		case 'updateMultiAction':
			return state.map(msg =>
				msg.multiAction?.id === action.id
					? { ...msg, multiAction: { ...msg.multiAction, ...action.updates } }
					: msg
			);
		case 'updateContactScrape':
			return state.map(msg =>
				msg.contactScrape?.id === action.id
					? { ...msg, contactScrape: { ...msg.contactScrape, ...action.updates } }
					: msg
			);
		case 'updateWriteProposal':
			return state.map((msg, i) =>
				i === action.index && msg.writeProposal
					? { ...msg, writeProposal: { ...msg.writeProposal, ...action.update } }
					: msg
			);
		case 'updateWriteProposalGroupItem': {
			const msg = state[action.messageIndex];
			const group = msg?.writeProposalGroup;
			if (!group) return state;
			const existing = group.items[action.itemIndex];
			if (!existing || existing.kind !== 'write') return state;
			const updatedProposal = { ...existing.proposal, ...action.update };
			const nextItems = group.items.map((it, i) =>
				i === action.itemIndex ? { kind: 'write' as const, proposal: updatedProposal } : it
			);
			return state.map((m, i) =>
				i === action.messageIndex ? { ...m, writeProposalGroup: { items: nextItems } } : m
			);
		}
		case 'updateWriteProposalGroupEmailItem': {
			const msg = state[action.messageIndex];
			const group = msg?.writeProposalGroup;
			if (!group) return state;
			const existing = group.items[action.itemIndex];
			if (!existing || existing.kind !== 'email') return state;
			const updatedDraft = { ...existing.draft, ...action.update };
			const nextItems = group.items.map((it, i) =>
				i === action.itemIndex ? { kind: 'email' as const, draft: updatedDraft } : it
			);
			return state.map((m, i) =>
				i === action.messageIndex ? { ...m, writeProposalGroup: { items: nextItems } } : m
			);
		}
		case 'updateEmailDraft':
			return state.map((msg, i) =>
				i === action.index && msg.emailDraft
					? { ...msg, emailDraft: { ...msg.emailDraft, ...action.update } }
					: msg
			);
		case 'updateOfferDraft':
			return state.map((msg, i) =>
				i === action.index && msg.offerDraft
					? { ...msg, offerDraft: { ...msg.offerDraft, ...action.update } }
					: msg
			);
		case 'updateToolProposal':
			return state.map((msg, i) =>
				i === action.index && msg.toolProposal
					? { ...msg, toolProposal: { ...msg.toolProposal, ...action.update } }
					: msg
			);
		case 'updateProspectingAdd':
			return state.map((msg, i) =>
				i === action.index && msg.prospectingAdd
					? { ...msg, prospectingAdd: { ...msg.prospectingAdd, ...action.update } }
					: msg
			);
		case 'setLastUserImages': {
			const updated = [...state];
			for (let i = updated.length - 1; i >= 0; i--) {
				if (updated[i].role === 'user' && updated[i].images) {
					updated[i] = { ...updated[i], images: action.urls };
					break;
				}
			}
			return updated;
		}
		case 'setLastUserAttachments': {
			const updated = [...state];
			for (let i = updated.length - 1; i >= 0; i--) {
				if (updated[i].role === 'user') {
					updated[i] = { ...updated[i], attachments: action.attachments };
					break;
				}
			}
			return updated;
		}
		case 'truncateFrom':
			return state.slice(0, action.index);
		case 'attachActivitiesToLastAssistant': {
			for (let i = state.length - 1; i >= 0; i--) {
				if (state[i].role === 'assistant') {
					const next = [...state];
					next[i] = { ...next[i], activities: action.activities };
					return next;
				}
			}
			return state;
		}
		case 'attachThinkingToLastAssistant': {
			for (let i = state.length - 1; i >= 0; i--) {
				if (state[i].role === 'assistant') {
					if (state[i].thinking) return state;
					const next = [...state];
					next[i] = { ...next[i], thinking: action.thinking };
					return next;
				}
			}
			return state;
		}
	}
};

export default messagesReducer;
export type { MessagesAction };
